/**
 * Lightweight error logger — posts runtime errors to Firestore `errorReports`
 * so beta crashes are visible without a Sentry account.
 *
 * Wired from App.jsx on mount. Attaches two globals:
 *   - window.onerror              → uncaught synchronous errors
 *   - unhandledrejection          → uncaught promise rejections
 *
 * Design notes:
 *   - Best-effort: writes are fire-and-forget with a catch() that swallows
 *     failures (logging an error while handling an error is a tarpit).
 *   - De-duped in-session on the (message, file, line) tuple so a single
 *     broken render doesn't write 400 docs before the user notices.
 *   - Throttled to 10 reports per session regardless of uniqueness.
 *   - Stack traces truncated to 3000 chars to stay under Firestore's 1 MB limit.
 *   - Redacts anything that looks like a Firebase ID token from the stack.
 */

import { collection, addDoc, serverTimestamp } from 'firebase/firestore'
import { db, auth } from '../firebase.js'

const SESSION_SEEN = new Set()
const MAX_REPORTS_PER_SESSION = 10
let reportCount = 0

function redact(str) {
  if (!str) return str
  return String(str)
    // Firebase ID tokens (long base64url JWTs)
    .replace(/eyJ[\w-]+\.[\w-]+\.[\w-]+/g, '[redacted-token]')
    // Data URLs (can balloon the doc)
    .replace(/data:[^,\s]+,[^\s)]+/g, '[redacted-datauri]')
}

async function report({ message, source, line, col, stack, kind, extra }) {
  if (reportCount >= MAX_REPORTS_PER_SESSION) return
  const dedupeKey = `${kind}|${message}|${source}|${line}`
  if (SESSION_SEEN.has(dedupeKey)) return
  SESSION_SEEN.add(dedupeKey)
  reportCount += 1

  try {
    await addDoc(collection(db, 'errorReports'), {
      kind,
      message:  redact(message).slice(0, 1000),
      source:   redact(source).slice(0, 500),
      line:     Number.isFinite(line) ? line : null,
      col:      Number.isFinite(col)  ? col  : null,
      stack:    redact(stack).slice(0, 3000),
      url:      window.location?.href?.slice(0, 500) || '',
      ua:       navigator.userAgent?.slice(0, 300) || '',
      uid:      auth.currentUser?.uid || null,
      email:    auth.currentUser?.email || null,
      ...(extra && Object.keys(extra).length ? { extra } : {}),
      createdAt: serverTimestamp(),
    })
  } catch {
    /* swallow — writing the error failed and we can't recurse on logging */
  }
}

/**
 * Called from the top-level ErrorBoundary so React render errors are visible
 * alongside uncaught globals. Stack comes from componentDidCatch's second arg.
 */
export function reportBoundaryError(err, info) {
  report({
    kind:    'react-boundary',
    message: err?.message || String(err || 'unknown'),
    source:  '',
    line:    null,
    col:     null,
    stack:   (err?.stack || '') + '\n\n' + (info?.componentStack || ''),
  })
}

let installed = false

export function installErrorLogger() {
  if (installed || typeof window === 'undefined') return
  installed = true

  window.addEventListener('error', (event) => {
    report({
      kind:    'uncaught-error',
      message: event.message || String(event.error?.message || 'unknown'),
      source:  event.filename || '',
      line:    event.lineno,
      col:     event.colno,
      stack:   event.error?.stack || '',
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    report({
      kind:    'unhandled-rejection',
      message: String(reason?.message || reason || 'unknown'),
      source:  '',
      line:    null,
      col:     null,
      stack:   reason?.stack || '',
    })
  })
}
